import { useState, useEffect, useCallback } from 'react'
import { doc, runTransaction, onSnapshot } from 'firebase/firestore'
import { db } from '@services/firebase'
import { useAuth } from '@contexts/AuthContext'
import { ENABLE_EVENT_INTERACTIONS } from '@config/features'

/**
 * Hook para gerenciar interações de eventos em tempo real com o Firestore.
 * @param {string} eventId - O ID do evento.
 */
export const useEventInteractions = (eventId) => {
  const { user, canInteract } = useAuth()
  const [interactions, setInteractions] = useState({ emojis: {}, rsvp: {} })
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    if (!ENABLE_EVENT_INTERACTIONS || !eventId) {
      setLoading(false)
      return
    }

    const eventDocRef = doc(db, 'events', eventId)

    // Adiciona um timeout para o estado de carregamento, útil para testes E2E ou problemas de conexão
    const timer = setTimeout(() => {
      setLoading(false)
    }, 3000)

    const unsubscribe = onSnapshot(
      eventDocRef,
      (docSnapshot) => {
        clearTimeout(timer)
        if (docSnapshot.exists()) {
          const data = docSnapshot.data()
          setInteractions({
            emojis: data.emojis || {},
            rsvp: data.rsvp || {},
          })
        } else {
          // Se o documento não existir, inicializa localmente
          setInteractions({ emojis: {}, rsvp: {} })
        }
        setLoading(false)
      },
      (err) => {
        clearTimeout(timer)
        console.error("Erro ao ouvir interações do evento:", err)
        setError(err)
        setLoading(false)
      }
    )

    return () => {
      unsubscribe()
      clearTimeout(timer)
    }
  }, [eventId])

  const toggleEmoji = useCallback(async (emojiId) => {
    if (!canInteract || !user) {
      throw new Error('Usuário não autenticado.')
    }

    const eventDocRef = doc(db, 'events', eventId)

    try {
      await runTransaction(db, async (transaction) => {
        const eventDoc = await transaction.get(eventDocRef)
        const currentData = eventDoc.data() || {}
        
        const emojis = currentData.emojis || {}
        const emojiData = emojis[emojiId] || { count: 0, users: [] }

        const userIndex = emojiData.users.indexOf(user.id)
        
        if (userIndex > -1) {
          // Usuário já reagiu, então remove
          emojiData.count -= 1
          emojiData.users.splice(userIndex, 1)
        } else {
          // Usuário ainda não reagiu, então adiciona
          emojiData.count += 1
          emojiData.users.push(user.id)
        }

        const newEmojis = { ...emojis, [emojiId]: emojiData }

        // Atualiza apenas o campo de emojis, para evitar problemas com outros campos
        transaction.set(eventDocRef, { emojis: newEmojis }, { merge: true })
      })
    } catch (e) {
      console.error("Erro ao atualizar emoji:", e)
      throw e
    }
  }, [canInteract, user, eventId])


  const toggleRSVP = useCallback(async (rsvpType) => {
    if (!canInteract || !user) {
      throw new Error('Usuário não autenticado.')
    }
    if (rsvpType !== 'done' && rsvpType !== 'interest') {
      throw new Error('Tipo de RSVP inválido.')
    }

    const eventDocRef = doc(db, 'events', eventId)
    
    const userProfile = {
      id: user.id,
      name: user.name || 'Anônimo',
      image_thumbnail: user.image_thumbnail || null,
    }

    try {
      await runTransaction(db, async (transaction) => {
        const eventDoc = await transaction.get(eventDocRef)
        const currentData = eventDoc.data() || {}
        
        const rsvp = currentData.rsvp || { done: { count: 0, users: [] }, interest: { count: 0, users: [] } }
        const otherType = rsvpType === 'done' ? 'interest' : 'done'

        let typeList = rsvp[rsvpType].users
        let otherList = rsvp[otherType].users
        
        const userIndex = typeList.findIndex(u => u.id === user.id)
        const otherIndex = otherList.findIndex(u => u.id === user.id)

        // Se o usuário estiver na outra lista, remova-o
        if (otherIndex > -1) {
          rsvp[otherType].count -= 1
          otherList.splice(otherIndex, 1)
        }

        if (userIndex > -1) {
          // Usuário já está na lista, então remove
          rsvp[rsvpType].count -= 1
          typeList.splice(userIndex, 1)
        } else {
          // Usuário não está na lista, então adiciona
          rsvp[rsvpType].count += 1
          typeList.push(userProfile)
        }

        transaction.set(eventDocRef, { ...currentData, rsvp }, { merge: true })
      })
    } catch (e) {
      console.error("Erro ao atualizar RSVP:", e)
      throw e
    }
  }, [canInteract, user, eventId])

  if (!ENABLE_EVENT_INTERACTIONS) {
    return { 
      interactions: { emojis: {}, rsvp: {} }, 
      loading: false, 
      error: null, 
      toggleEmoji: async () => {}, 
      toggleRSVP: async () => {} 
    }
  }

  return { interactions, loading, error, toggleEmoji, toggleRSVP }
}
